import { Component, OnInit, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Storage } from '@angular/fire/storage';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Venue } from '../models/venue';
import { map } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { RecentVenuesService, RecentVenueEntry } from '../services/recent-venues.service';
import { VenueDataService } from '../services/venue-data.service';
import { environment } from '../../environments/environment';
import {
  GetVenueActivityResult,
  VenueActivityDataset,
  VenueActivityEntry,
} from '@blazebite/shared-models';

// Standard processTransaction envelope around the callable's data payload
interface GetVenueActivityEnvelope {
  success: boolean;
  duration: number;
  timestamp: string;
  data: GetVenueActivityResult;
}

// Venue document optionally enriched with the cached activity entry
export interface VenueRow extends Venue {
  activity?: VenueActivityEntry;
}

const USE_ACTIVITY_DATA_KEY = 'venue-list-use-activity-data';

// Last activity dataset seen, so the Orders column and activity ordering paint
// on the first frame instead of ~300 ms later when the callable returns (which
// also reorders the table under the user). The backend already serves this
// dataset from its own cache, so a local copy is no staler than a fresh call.
const ACTIVITY_CACHE_KEY = `venue-list:${environment.projectId}:activity:v1`;

function readActivityCache(): VenueActivityDataset | null {
  try {
    const raw = localStorage.getItem(ACTIVITY_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && Array.isArray(parsed.venues) ? (parsed as VenueActivityDataset) : null;
  } catch {
    return null;
  }
}

function writeActivityCache(dataset: VenueActivityDataset): void {
  try {
    localStorage.setItem(ACTIVITY_CACHE_KEY, JSON.stringify(dataset));
  } catch {
    // Quota / private mode — the callable path still works without the cache.
  }
}

// Neutral placeholder for venues with no logo or a broken image URL. An inline
// data URI can't itself fail to load, so the error handler can't loop.
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
    '<rect width="40" height="40" fill="#e5e7eb"/>' +
    '<path d="M12 27l6-8 4 5 3-3 5 6z" fill="#9ca3af"/>' +
    '<circle cx="27" cy="13" r="3" fill="#9ca3af"/></svg>'
  );

@Component({
    selector: 'app-venue-list',
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './venue-list.component.html',
    styleUrls: ['./venue-list.component.css']
})
export class VenueListComponent implements OnInit {
  private storage = inject(Storage);
  private functions = inject(Functions);
  private recentVenuesService = inject(RecentVenuesService);
  private venueData = inject(VenueDataService);

  venues$: Observable<Venue[]>;
  filteredVenues$: Observable<VenueRow[]>;
  searchTerm = '';
  showEnabledOnly = false;
  private searchTerm$ = new BehaviorSubject<string>('');
  private showEnabledOnly$ = new BehaviorSubject<boolean>(false);

  // Venue activity overlay (cached dataset from superAdminExperience-getVenueActivity).
  // Preference persists in localStorage, defaulted off.
  useActivityData = false;
  activityLoading = false;
  activityError: string | null = null;
  activityDataset: VenueActivityDataset | null = null;
  activityFromCache = false;
  private useActivityData$ = new BehaviorSubject<boolean>(false);
  private activityByVenueId$ = new BehaviorSubject<Map<string, VenueActivityEntry> | null>(null);

  // Recently visited venues
  recentVenues: RecentVenueEntry[] = [];

  readonly placeholderImage = PLACEHOLDER_IMAGE;

  constructor() {
    this.venues$ = this.venueData.getVenues();

    this.filteredVenues$ = combineLatest([
      this.venues$,
      this.searchTerm$,
      this.showEnabledOnly$,
      this.useActivityData$,
      this.activityByVenueId$
    ]).pipe(
      map(([venues, searchTerm, enabledOnly, useActivity, activityMap]) => {
        const activityOn = useActivity && activityMap !== null;

        let rows: VenueRow[] = activityOn
          ? venues.map(venue => ({ ...venue, activity: activityMap!.get(venue.id) }))
          : venues;

        if (enabledOnly) {
          rows = rows.filter(venue => venue.enabled);
        }

        if (searchTerm.trim()) {
          const term = searchTerm.toLowerCase();
          rows = rows.filter(venue => {
            if (venue.name?.toLowerCase().includes(term)) return true;
            // With activity data on, search also matches venue email and contacts
            if (venue.activity) {
              return venue.activity.email.toLowerCase().includes(term) ||
                venue.activity.contacts.some(contact =>
                  contact.displayName.toLowerCase().includes(term) ||
                  contact.email.toLowerCase().includes(term)
                );
            }
            return false;
          });
        }

        if (activityOn) {
          // Most active venues first, then alphabetical for the zero-order tail
          rows = [...rows].sort((a, b) =>
            (b.activity?.orderCounts.total ?? 0) - (a.activity?.orderCounts.total ?? 0) ||
            (a.name || '').localeCompare(b.name || '')
          );
        }

        return rows;
      })
    );
  }

  ngOnInit(): void {
    this.recentVenuesService.recentVenues$.subscribe(entries => {
      this.recentVenues = entries;
    });

    this.useActivityData = localStorage.getItem(USE_ACTIVITY_DATA_KEY) === 'true';
    this.useActivityData$.next(this.useActivityData);
    if (this.useActivityData) {
      this.loadActivityData();
    }
  }

  onActivityToggleChange(): void {
    localStorage.setItem(USE_ACTIVITY_DATA_KEY, String(this.useActivityData));
    this.useActivityData$.next(this.useActivityData);
    if (this.useActivityData && !this.activityDataset && !this.activityLoading) {
      this.loadActivityData();
    }
  }

  private async loadActivityData(): Promise<void> {
    // Seed from the last dataset seen so the first paint is already complete;
    // the callable's response replaces it below.
    if (!this.activityDataset) {
      const cached = readActivityCache();
      if (cached) this.applyActivityDataset(cached, true);
    }

    // Spinner only when there's nothing to show yet — a refresh behind an
    // already-populated table is silent, like the venue rows themselves.
    this.activityLoading = !this.activityDataset;
    this.activityError = null;

    try {
      const getVenueActivity = httpsCallable<{ forceRefresh?: boolean }, GetVenueActivityEnvelope>(
        this.functions,
        'superAdminExperience-getVenueActivity'
      );
      const result = await getVenueActivity({});

      this.applyActivityDataset(result.data.data.dataset, result.data.data.fromCache);
      writeActivityCache(result.data.data.dataset);
    } catch (error: any) {
      console.error('Error fetching venue activity:', error);
      this.activityError = error.message || 'Failed to fetch venue activity.';
    } finally {
      this.activityLoading = false;
    }
  }

  private applyActivityDataset(dataset: VenueActivityDataset, fromCache: boolean): void {
    this.activityDataset = dataset;
    this.activityFromCache = fromCache;
    const byId = new Map<string, VenueActivityEntry>();
    dataset.venues.forEach(entry => byId.set(entry.venueID, entry));
    this.activityByVenueId$.next(byId);
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm);
  }

  onEnabledFilterChange(): void {
    this.showEnabledOnly$.next(this.showEnabledOnly);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchTerm$.next('');
  }

  barHeightPct(activity: VenueActivityEntry, count: number): number {
    const max = Math.max(...activity.orderCounts.byDay.map(day => day.count), 1);
    return Math.max((count / max) * 100, count > 0 ? 8 : 0);
  }

  dayLabel(date: string): string {
    // date is YYYY-MM-DD in the venue's timezone; parse as local to avoid UTC shift
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
  }

  getPortalUrl(venueId: string): string {
    return `${environment.portalBaseUrl}/home/realtimereports/${venueId}`;
  }

  getOrdersUrl(venueId: string): string {
    return `${environment.ordersBaseUrl}/venue/${venueId}`;
  }

  onPortalClick(venueId: string, name: string | undefined, imagePath: string | undefined, enabled: boolean | undefined): void {
    this.recentVenuesService.trackVisit(venueId, name ?? '', imagePath ?? '', enabled ?? false);
    // Portal opens in a new tab, so refresh the recent list shown on this page
    this.recentVenues = this.recentVenuesService.getRecentVenues();
  }

  removeRecentVenue(venueId: string): void {
    this.recentVenuesService.removeVenue(venueId);
    this.recentVenues = this.recentVenuesService.getRecentVenues();
  }

  trackById(index: number, item: Venue): string {
    return item.id;
  }

  /** Swap a 404'd / malformed logo for the placeholder instead of a broken-image icon. */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && img.src !== PLACEHOLDER_IMAGE) img.src = PLACEHOLDER_IMAGE;
  }

}
